/* eslint-disable no-undef */
import {
    useContext, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { TableChangeState, TableChangeType } from 'react-bootstrap-table-next';
import { SearchMatchProps } from 'react-bootstrap-table2-toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { MultiValue } from 'react-select';
import Swal from 'sweetalert2';
import { LayoutContext } from '../../contexts/layout';
import { updateDataLink } from '../../helpers/general';
import { layoutStateSelector } from '../../redux/selectors/layouts';
import { ITableDataResponse, ITableRequestParams } from '../../types/components/table';
import { ITableColumn, ITableLayout } from '../../types/layout/table';
import useRest from '../useRest';
import { tableStateSelector } from '../../redux/selectors/tables';
import { setTableFilters, setTableScroll } from '../../redux/actions/tables';
// import dataJSON from './data.json';

type TTableNewState = TableChangeState<{}> & SearchMatchProps;

const useTable = ({
    actions, dataLink, dataIndex, defaultPageSize, columns, primaryKey, collapsed: prdefinedCollapsed,
}: ITableLayout) => {
    const [response, setResponse] = useState<ITableDataResponse>();
    const [dataLoading, setDataLoading] = useState(false);
    const [tableColumns, setTableColumns] = useState<ITableColumn[]>([]);
    const [collapsed, setCollapsed] = useState(!!prdefinedCollapsed);

    const [searchParams, setSearchParams] = useSearchParams();

    // @ts-ignore
    const [page, setPage] = useState(typeof searchParams.get('page') === 'string' ? parseInt(searchParams.get('page'), 10) : 1);
    // @ts-ignore
    const [sizePerPage, setSizePerPage] = useState(typeof searchParams.get('sizePerPage') === 'string' ? parseInt(searchParams.get('sizePerPage'), 10) : defaultPageSize || 100);

    const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder'));
    const [sortColumn, setSortColumn] = useState(searchParams.get('sortColumn'));

    const [search, setSearch] = useState(searchParams.get('search'));

    const dispatch = useDispatch();

    const rest = useRest();

    const { layoutId } = useContext(LayoutContext);

    const { objectId: idOwner } = useParams();
    const currPageUrl = new URL(window.location.href);

    const dataIndexAccepted = useRef(false);

    let tableName = '';

    if (tableName.length === 0 && typeof dataLink === 'string') {
        tableName = dataLink.replace(/[^a-zA-Z0-9_-]/g, '_');
        if (tableName[0] === '_') {
            tableName = tableName.slice(1, tableName.length);
        }
    }

    if (tableName.length === 0 && typeof dataIndex === 'string') {
        tableName = dataIndex;
    }

    if (searchParams.get('type')) {
        tableName += `_${searchParams.get('type')}`;
    }

    const layoutState = useSelector(layoutStateSelector(layoutId));
    const tableState = useSelector(tableStateSelector(tableName));

    const scrollEventAttached = useRef(false);
    const scrollTimeout = useRef<NodeJS.Timeout>();
    const tableHtml = useRef<any>();

    const insideForm = false; // TODO

    const tableParams = {
        sizePerPage,
        page,
        search,
        sortOrder,
        sortColumn,
    };

    const getData = async (ts?: TTableNewState, fromDataIndex?: boolean) => {
        if (typeof dataLink === 'string' && !fromDataIndex) {
            setDataLoading(true);

            const body: ITableRequestParams = {
                pageSize: tableParams.sizePerPage,
                page: tableParams.page,
            };

            if (tableParams.sortColumn && tableParams.sortOrder) {
                body.sort = {
                    column: tableParams.sortColumn,
                    order: tableParams.sortOrder,
                };
            }

            if (typeof search === 'string' && search.length) {
                body.search = search;
            }

            if (tableState && tableState instanceof Object) {
                if (tableState.filters instanceof Object) {
                    const { filters } = tableState;
                    const filtersKeys = Object.keys(tableState.filters);

                    if (filtersKeys.length > 0) {
                        body.filters = filtersKeys.map((key) => ({
                            column: key,
                            value: filters[key].filterVal,
                        }));
                    }
                }
            }

            if (typeof ts?.searchText === 'string' && ts.searchText.length > 0) {
                body.search = ts.searchText;
            }

            if (typeof idOwner === 'string') {
                currPageUrl.searchParams.append('idOwner', idOwner);
            }

            currPageUrl.searchParams.delete('page');
            currPageUrl.searchParams.delete('sizePerPage');
            currPageUrl.searchParams.delete('sortColumn');
            currPageUrl.searchParams.delete('sortOrder');
            currPageUrl.searchParams.delete('search');

            const result = await rest.fetch({
                objectName: dataLink + currPageUrl.search,
                method: 'post',
                data: body,
            });

            if (!result.isError && result.response?.data && typeof result.response.data.totalCount === 'number' && Array.isArray(result.response.data.data)) {
                setResponse(result.response.data);
            } else {
                setResponse(undefined);
            }

            setDataLoading(false);
        } else if (typeof dataIndex === 'string' && layoutState.data) {
            if (Array.isArray(layoutState.data[dataIndex])) {
                setResponse({
                    data: layoutState.data[dataIndex],
                    totalCount: layoutState.data[dataIndex].length,
                    summary: layoutState.summary,
                });
            } else if (layoutState.data[dataIndex] instanceof Object) {
                setResponse({
                    data: layoutState.data[dataIndex].data,
                    totalCount: layoutState.data[dataIndex].totalCount,
                    summary: layoutState.data[dataIndex].summary,
                });
            }
            dataIndexAccepted.current = true;
        }
    };

    const onColumnsSelectorChange = (e: MultiValue<{ label: string; value: string; }>) => {
        const nextState = e.map((item) => columns.find((el) => el.dataIndex === item.value)).filter((item) => typeof item !== 'undefined');
        // @ts-ignore
        setTableColumns(nextState);
    };

    const onTableChange = (type: TableChangeType | 'search', newState: TTableNewState) => {
        switch (type) {
            case 'filter': {
                dispatch(setTableFilters(tableName, newState.filters));
                break;
            }
            case 'pagination': {
                if (!insideForm) {
                    searchParams.set('page', newState.page.toString());
                    searchParams.set('sizePerPage', newState.sizePerPage.toString());
                    setSearchParams(searchParams);
                }
                setPage(newState.page);
                setSizePerPage(newState.sizePerPage);
                break;
            }
            case 'search': {
                if (!insideForm) {
                    if (typeof newState.searchText === 'string' && newState.searchText.length) {
                        searchParams.set('search', newState.searchText);
                    } else {
                        searchParams.delete('search');
                    }
                    setSearchParams(searchParams);
                }
                setSearch(newState.searchText);
                break;
            }
            case 'sort': {
                if (!insideForm) {
                    searchParams.set('sortColumn', newState.sortField);
                    searchParams.set('sortOrder', newState.sortOrder);
                    setSearchParams(searchParams);
                }
                setSortColumn(newState.sortField);
                setSortOrder(newState.sortOrder);
                break;
            }
        }
    };

    const onTableScroll = (x: any) => {
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => {
            if (tableName.length) {
                dispatch(setTableScroll(tableName, { y: x.target.scrollTop }));
            }
        }, 500);
    };

    const rowDelete = async (row: Record<string, any>) => {
        if (typeof actions?.delete === 'string') {
            const alertResult = await Swal.fire({
                title: 'Подтвердите действие',
                text: 'Вы действительно хотите удалить запись?',
                confirmButtonText: 'Да, удалить',
                cancelButtonText: 'Отмена',
                icon: 'question',
                showCancelButton: true,
            });

            if (alertResult.isConfirmed) {
                setDataLoading(true);

                await rest.fetch({
                    method: 'post',
                    objectName: updateDataLink(actions.delete, row[primaryKey]),
                    data: {
                        idOwner,
                    },
                });

                await getData();

                setDataLoading(false);
            }
        }
    };

    useEffect(() => {
        if (dataIndexAccepted.current) {
            getData();
        }
    }, [
        page,
        sizePerPage,
        sortColumn,
        sortOrder,
        search,
        JSON.stringify(tableState?.filters),
    ]);

    useEffect(() => {
        setTableColumns(columns);
    }, [columns]);

    useEffect(() => () => {
        dataIndexAccepted.current = false;
    }, [searchParams.get('type')]);

    useEffect(() => {
        if (typeof dataIndex === 'string' && layoutState && layoutState.data && layoutState.data[dataIndex]) {
            getData(undefined, true);
        } else {
            dataIndexAccepted.current = true;
            getData();
        }
    }, [dataLink, layoutState?.data?.[dataIndex as string]]);

    useLayoutEffect(() => {
        if (tableName.length) {
            const html = document.querySelector(`.table-body-with-scroll.${tableName}`);
            if (html) {
                if (typeof tableState?.scroll?.y === 'number') {
                    html.scrollTo({ top: tableState.scroll.y });
                }
                if (!scrollEventAttached.current) {
                    tableHtml.current = html;
                    scrollEventAttached.current = true;
                    html.addEventListener('scroll', onTableScroll);
                }
            }
        }
        return () => {};
    });

    return {
        columns: tableColumns,
        data: response?.data,
        dataLoading,
        onColumnsSelectorChange,
        onTableChange,
        totalCount: response?.totalCount,
        rowDelete,
        insideForm,
        collapsed,
        onCollapseClick: () => setCollapsed(!collapsed),
        getData,
        idOwner,
        tableName,
        tableParams,
        summary: response?.summary,
    };
};

export default useTable;
